import { CalendarDays, ChevronDown, ChevronUp, Eye, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import WorkerPremiumCard, { WorkerPremiumCardHeader } from './WorkerPremiumCard';
import {
  WORKER_STUCK_ATTENDANCE_MESSAGE,
  getWorkerRejectedAttendanceCardLabel,
  getWorkerRejectedAttendanceMessage,
  getWorkerShiftAttendanceStatusFromItem,
  hasWorkerStuckOpenAttendance,
} from './workerShiftUtils';

function MyShiftCard({
  item,
  variant,
  formatDate,
  formatDateTime,
  canWorkerClockIn,
  canWorkerClockOut,
  canWorkerReClock,
  workerShiftClocking,
  onAccept,
  onReject,
  onClockIn,
  onClockOut,
  onReClock,
  onView,
}) {
  const shift = item.shift || {};
  const responseStatus = item.worker_response_status || item?.assignment?.worker_response_status || 'pending';
  const assignmentStatus = item.assignment_status || item?.assignment?.status || 'active';
  const attendanceStatus = getWorkerShiftAttendanceStatusFromItem(item);
  const requiresDailyNote = Boolean(shift.service_user_id || shift.service_user?.id);
  const dailyNoteStatus = item.current_daily_note
    ? 'saved'
    : (requiresDailyNote ? 'pending' : 'not required');
  const canRespond = variant === 'pending_offer'
    && item.assignment_status === 'active'
    && (responseStatus === 'pending' || !responseStatus);
  const isHistory = variant === 'history';
  const cardClass = isHistory
    ? 'rounded-xl border border-slate-100 bg-slate-50/80 p-3'
    : 'rounded-xl border border-slate-200/80 bg-gradient-to-br from-white to-slate-50/50 p-3 shadow-sm';

  const primaryStatusLabel = isHistory && attendanceStatus === 'approved'
    ? 'Attendance approved'
    : isHistory && (assignmentStatus === 'completed' || shift.status === 'completed')
      ? 'Completed'
      : attendanceStatus === 'approved' ? 'Approved' :
        attendanceStatus === 'submitted' ? 'Awaiting admin review' :
        attendanceStatus === 'rejected' ? getWorkerRejectedAttendanceCardLabel(item.current_attendance) :
        hasWorkerStuckOpenAttendance(item) ? WORKER_STUCK_ATTENDANCE_MESSAGE :
        attendanceStatus === 'open' ? 'Shift in progress' :
        assignmentStatus;

  const primaryBadgeClass = isHistory
    ? 'bg-green-50 text-green-800 border border-green-100'
    : attendanceStatus === 'approved' ? 'bg-green-100 text-green-700' :
      attendanceStatus === 'submitted' ? 'bg-blue-100 text-blue-700' :
      attendanceStatus === 'rejected' ? 'bg-red-100 text-red-700' :
      hasWorkerStuckOpenAttendance(item) ? 'bg-orange-100 text-orange-800 border border-orange-200' :
      attendanceStatus === 'open' ? 'bg-amber-100 text-amber-700' :
      assignmentStatus === 'cancelled' ? 'bg-slate-100 text-slate-700' :
      assignmentStatus === 'completed' ? 'bg-green-100 text-green-700' :
      'bg-slate-100 text-slate-700';

  return (
    <div key={item.assignment_id || shift.id} className={cardClass} data-testid={`my-shift-${variant}-${shift.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className={`font-medium ${isHistory ? 'text-slate-700' : 'text-slate-900'}`}>
            {shift.location_text || 'Location pending'}
          </p>
          {shift.care_location ? (
            <p className="mt-1 text-xs text-slate-600">
              {shift.care_location.name}
              {shift.care_location.address_line_1 ? `, ${shift.care_location.address_line_1}` : ''}
              {shift.care_location.city ? `, ${shift.care_location.city}` : ''}
            </p>
          ) : null}
          <p className="mt-1 text-xs text-slate-600">
            {formatDate(shift.start_at)} • {formatDateTime(shift.start_at).split(', ').pop()} - {formatDateTime(shift.end_at).split(', ').pop()}
          </p>
          <p className="mt-1 text-xs text-slate-600">Role: {shift.role_required || '\u2014'}</p>
          {isHistory ? (
            <p className="mt-1 text-xs text-slate-500">This shift is in your history — view only for reference.</p>
          ) : null}
          {isHistory && attendanceStatus === 'approved' && item.current_attendance?.approval_tier === 'corrected' ? (
            <p className="mt-1 text-xs text-slate-500">Corrected by your care provider</p>
          ) : null}
          {!isHistory && shift.notes ? <p className="mt-1 text-xs text-slate-500 line-clamp-2">Notes: {shift.notes}</p> : null}
          {!isHistory && item.worker_response_note ? (
            <p className="mt-1 text-xs text-slate-500 line-clamp-2">Your note: {item.worker_response_note}</p>
          ) : null}
          {!isHistory && item.cancellation_reason ? (
            <p className="mt-1 text-xs text-red-700 line-clamp-2">Cancellation reason: {item.cancellation_reason}</p>
          ) : null}
          {!isHistory && attendanceStatus === 'rejected' && getWorkerRejectedAttendanceMessage(item.current_attendance) && (
            <p className="mt-1 text-xs text-red-700 font-medium">{getWorkerRejectedAttendanceMessage(item.current_attendance)}</p>
          )}
          {!isHistory && attendanceStatus === 'rejected' && item.current_attendance?.review_note && (
            <p className="mt-1 text-xs text-red-600 line-clamp-2">Reason: {item.current_attendance.review_note}</p>
          )}
          {!isHistory && hasWorkerStuckOpenAttendance(item) && (
            <p className="mt-1 text-xs font-medium text-orange-800">{WORKER_STUCK_ATTENDANCE_MESSAGE}</p>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-2">
          <Badge className={primaryBadgeClass}>{primaryStatusLabel}</Badge>
          {!isHistory && variant !== 'pending_offer' && (
            <Badge className={
              responseStatus === 'accepted'
                ? 'bg-green-100 text-green-700'
                : responseStatus === 'rejected'
                  ? 'bg-red-100 text-red-700'
                  : 'bg-slate-100 text-slate-700'
            }>
              {responseStatus === 'accepted' ? 'Accepted' : responseStatus === 'rejected' ? 'Rejected' : 'Awaiting response'}
            </Badge>
          )}
          {variant === 'pending_offer' && (
            <Badge className="bg-amber-100 text-amber-800">Awaiting your response</Badge>
          )}
          {!isHistory && requiresDailyNote && dailyNoteStatus === 'saved' && (
            <Badge className="bg-green-100 text-green-700">Daily note: saved</Badge>
          )}
          {!isHistory && requiresDailyNote && dailyNoteStatus === 'pending' && (
            <Badge className="bg-amber-100 text-amber-700">Daily note: pending</Badge>
          )}
          {canRespond && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" className="min-h-10 touch-manipulation" onClick={() => onAccept(item)}>
                Accept
              </Button>
              <Button variant="destructive" size="sm" className="min-h-10 touch-manipulation" onClick={() => onReject(item)}>
                Reject
              </Button>
            </div>
          )}
          {!isHistory && canWorkerClockIn(item) && (
            <Button size="sm" className="min-h-10 bg-emerald-600 hover:bg-emerald-700 text-white touch-manipulation" onClick={() => onClockIn(item)} disabled={workerShiftClocking}>
              Clock In
            </Button>
          )}
          {!isHistory && canWorkerClockOut(item) && (
            <Button size="sm" className="min-h-10 bg-amber-600 hover:bg-amber-700 text-white touch-manipulation" onClick={() => onClockOut(item)} disabled={workerShiftClocking}>
              Clock Out
            </Button>
          )}
          {!isHistory && canWorkerReClock(item) && (
            <Button size="sm" variant="outline" className="min-h-10 border-blue-300 text-blue-700 hover:bg-blue-50 touch-manipulation" onClick={() => onReClock(item)} disabled={workerShiftClocking}>
              Re-submit
            </Button>
          )}
          <Button variant="outline" size="sm" className="min-h-10 touch-manipulation" onClick={() => onView(shift.id)}>
            <Eye className="mr-1 h-3.5 w-3.5" />
            View
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function WorkerMyShiftsCard({
  loading,
  shiftGroups,
  showPastShifts,
  onTogglePastShifts,
  formatDate,
  formatDateTime,
  canWorkerClockIn,
  canWorkerClockOut,
  canWorkerReClock,
  workerShiftClocking,
  onAccept,
  onReject,
  onClockIn,
  onClockOut,
  onReClock,
  onView,
}) {
  return (
    <WorkerPremiumCard accent="purple" data-testid="worker-my-shifts-card">
      <WorkerPremiumCardHeader
        icon={CalendarDays}
        title="My Shifts"
        description="Current work and shift offers. Past shifts are kept separately for reference."
      />
      <div className="space-y-4">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        ) : (
          <>
            {shiftGroups.pending.length > 0 && (
              <div className="space-y-2" data-testid="my-shifts-pending-offers">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Pending shift offers</p>
                {shiftGroups.pending.map((item) => (
                  <MyShiftCard
                    key={item.assignment_id || item.shift?.id}
                    item={item}
                    variant="pending_offer"
                    formatDate={formatDate}
                    formatDateTime={formatDateTime}
                    canWorkerClockIn={canWorkerClockIn}
                    canWorkerClockOut={canWorkerClockOut}
                    canWorkerReClock={canWorkerReClock}
                    workerShiftClocking={workerShiftClocking}
                    onAccept={onAccept}
                    onReject={onReject}
                    onClockIn={onClockIn}
                    onClockOut={onClockOut}
                    onReClock={onReClock}
                    onView={onView}
                  />
                ))}
              </div>
            )}

            <div className="space-y-2" data-testid="my-shifts-active">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">Current &amp; upcoming</p>
              {shiftGroups.active.length === 0 ? (
                <p className="text-sm text-slate-600">No upcoming assigned shifts.</p>
              ) : (
                shiftGroups.active.map((item) => (
                  <MyShiftCard
                    key={item.assignment_id || item.shift?.id}
                    item={item}
                    variant="active"
                    formatDate={formatDate}
                    formatDateTime={formatDateTime}
                    canWorkerClockIn={canWorkerClockIn}
                    canWorkerClockOut={canWorkerClockOut}
                    canWorkerReClock={canWorkerReClock}
                    workerShiftClocking={workerShiftClocking}
                    onAccept={onAccept}
                    onReject={onReject}
                    onClockIn={onClockIn}
                    onClockOut={onClockOut}
                    onReClock={onReClock}
                    onView={onView}
                  />
                ))
              )}
            </div>

            {shiftGroups.history.length > 0 && (
              <div className="rounded-xl border border-slate-100 bg-slate-50/50" data-testid="my-shifts-history">
                <button
                  type="button"
                  onClick={onTogglePastShifts}
                  className="flex w-full items-center justify-between px-3 py-2.5 text-left touch-manipulation"
                >
                  <div>
                    <p className="text-sm font-medium text-slate-800">Recent shift history</p>
                    <p className="text-xs text-slate-500">{shiftGroups.history.length} past shift{shiftGroups.history.length !== 1 ? 's' : ''}</p>
                  </div>
                  {showPastShifts ? (
                    <ChevronUp className="h-4 w-4 text-slate-500 shrink-0" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-slate-500 shrink-0" />
                  )}
                </button>
                {showPastShifts && (
                  <div className="space-y-2 border-t border-slate-100 px-3 pb-3 pt-2">
                    {shiftGroups.history.map((item) => (
                      <MyShiftCard
                        key={item.assignment_id || item.shift?.id}
                        item={item}
                        variant="history"
                        formatDate={formatDate}
                        formatDateTime={formatDateTime}
                        canWorkerClockIn={canWorkerClockIn}
                        canWorkerClockOut={canWorkerClockOut}
                        canWorkerReClock={canWorkerReClock}
                        workerShiftClocking={workerShiftClocking}
                        onAccept={onAccept}
                        onReject={onReject}
                        onClockIn={onClockIn}
                        onClockOut={onClockOut}
                        onReClock={onReClock}
                        onView={onView}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </WorkerPremiumCard>
  );
}
